<template>
  <div class="special-case-container">
    <!-- 顶部横幅 -->
    <div class="banner-section">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('caseStudies.specialContainerCases') }}</h1>
        <p class="banner-subtitle">{{ $t('specialContainer.bannerSubtitle') }}</p>
        <div class="banner-desc">
          {{ $t('specialContainer.bannerDescription') }}
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h3>{{ $t('common.error') }}</h3>
      <p>{{ error }}</p>
      <button @click="fetchCaseData" class="retry-btn">{{ $t('common.reset') }}</button>
    </div>

    <!-- 案例详情 -->
    <div v-else-if="caseData" class="case-detail">
      <div class="container">
        <div class="case-header">
          <h2 class="case-title">{{ caseData.title }}</h2>
          <div class="case-meta">
            <span class="case-category">{{ caseData.category }}</span>
            <span class="case-date">{{ caseData.status }}</span>
          </div>
        </div>

        <!-- 客户背景 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('specialContainer.case.challenge') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="section-content">
            <p>{{ caseData.challenge.description }}</p>
          </div>
        </div>

        <!-- 专业解决方案 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('specialContainer.case.solution') }}</h3>
            <div class="title-decoration"></div>
          </div>
          
          <div v-if="caseData.solutions && caseData.solutions.length > 0" class="solution-grid">
            <div class="solution-item" v-for="(solution, index) in caseData.solutions" :key="index">
              <div class="solution-icon">
                <i :class="solution.icon"></i>
              </div>
              <div class="solution-content">
                <h4>{{ solution.title }}</h4>
                <p>{{ solution.description }}</p>
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('specialContainer.case.noSolutionData') }}</p>
          </div>
        </div>

        <!-- 案例图片展示 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('specialContainer.case.imageGallery') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="image-gallery">
            <div class="gallery-item" v-for="(image, index) in caseData.images" :key="index">
              <img :src="image.src" :alt="image.alt" />
              <div class="image-caption">{{ image.caption }}</div>
            </div>
          </div>
        </div>

        <!-- 成果与价值 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('specialContainer.case.results') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="result-content">
            <div class="result-card" v-for="(result, index) in caseData.results" :key="index">
              <div class="result-icon">
                <i :class="result.icon"></i>
              </div>
              <div class="result-text">
                <h4>{{ result.title }}</h4>
                <p>{{ result.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 技术参数 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('specialContainer.case.specifications') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="tech-specs">
            <div class="specs-grid">
              <div class="spec-item" v-for="(spec, index) in caseData.specifications" :key="index">
                <span class="spec-label">{{ spec.label }}</span>
                <span class="spec-value">{{ spec.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 联系我们 -->
    <CallMe />
    <MobileCallme />
  </div>
</template>

<script>
import CallMe from '@/components/callme/index'
import MobileCallme from '@/components/callme/MobileCallme.vue'
import { getCaseList } from '@/api/special-container-case'

export default {
  name: 'SpecialContainerCase',
  data() {
    return {
      caseData: null,
      loading: true,
      error: null
    }
  },
  components: {
    CallMe,
    MobileCallme
  },
  methods: {
    // 解析解决方案字段，兼容多种格式
    parseSolutionsField(solutionsField) {
      if (!solutionsField) return []
      
      try {
        const parsed = JSON.parse(solutionsField)
        
        // 如果是数组格式，直接返回
        if (Array.isArray(parsed)) {
          return parsed
        }
        
        // 如果是对象格式，转换为数组格式
        if (typeof parsed === 'object' && parsed !== null) {
          const solutions = []
          
          // 处理steps数组
          if (parsed.steps && Array.isArray(parsed.steps)) {
            parsed.steps.forEach((step, index) => {
              solutions.push({
                icon: this.getSolutionIcon(index),
                title: this.getSolutionTitle(index),
                description: step
              })
            })
          }
          
          // 如果没有steps但有description，创建一个默认解决方案
          if (solutions.length === 0 && parsed.description) {
            solutions.push({
              icon: 'el-icon-ship',
              title: this.$t('specialContainer.case.professionalTransportation'),
              description: parsed.description
            })
          }
          
          return solutions
        }
        
        return []
      } catch (e) {
        console.warn('解决方案字段解析失败:', e)
        return []
      }
    },
    
    // 解析成果字段，兼容多种格式
    parseResultsField(resultsField) {
      if (!resultsField) return []
      
      try {
        const parsed = JSON.parse(resultsField)
        
        // 如果是数组格式，直接返回
        if (Array.isArray(parsed)) {
          return parsed
        }
        
        // 如果是对象格式，转换为数组格式
        if (typeof parsed === 'object' && parsed !== null) {
          const results = []
          
          // 处理achievements数组
          if (parsed.achievements && Array.isArray(parsed.achievements)) {
            parsed.achievements.forEach((achievement, index) => {
              results.push({
                icon: this.getResultIcon(index),
                title: this.getResultTitle(index),
                description: achievement
              })
            })
          }
          
          // 如果没有achievements但有description，创建一个默认成果
          if (results.length === 0 && parsed.description) {
            results.push({
              icon: 'el-icon-success',
              title: this.$t('specialContainer.case.successfulDelivery'),
              description: parsed.description
            })
          }
          
          return results
        }
        
        return []
      } catch (e) {
        console.warn('成果字段解析失败:', e)
        return []
      }
    },
    
    // 解析技术参数字段，兼容多种格式
    parseSpecificationsField(specificationsField) {
      if (!specificationsField) return []
      
      try {
        const parsed = JSON.parse(specificationsField)
        
        // 如果是数组格式，直接返回
        if (Array.isArray(parsed)) {
          return parsed
        }
        
        // 如果是对象格式，转换为数组格式
        if (typeof parsed === 'object' && parsed !== null) {
          const specifications = []
          
          // 将对象转换为数组格式
          Object.entries(parsed).forEach(([key, value]) => {
            specifications.push({
              label: this.getSpecificationLabel(key),
              value: value
            })
          })
          
          return specifications
        }
        
        return []
      } catch (e) {
        console.warn('技术参数字段解析失败:', e)
        return []
      }
    },
    
    // 获取解决方案图标
    getSolutionIcon(index) {
      const icons = ['el-icon-ship', 'el-icon-box', 'el-icon-setting', 'el-icon-time', 'el-icon-location']
      return icons[index % icons.length] || 'el-icon-ship'
    },
    
    // 获取解决方案标题
    getSolutionTitle(index) {
      const titles = [
        this.$t('specialContainer.case.solutionTypeSelection'),
        this.$t('specialContainer.case.professionalLoading'),
        this.$t('specialContainer.case.transportationSafety'),
        this.$t('specialContainer.case.timeManagement'),
        this.$t('specialContainer.case.routePlanning')
      ]
      return titles[index % titles.length] || this.$t('specialContainer.case.professionalSolution')
    },
    
    // 获取成果图标
    getResultIcon(index) {
      const icons = ['el-icon-success', 'el-icon-star-on', 'el-icon-medal', 'el-icon-trophy', 'el-icon-flag']
      return icons[index % icons.length] || 'el-icon-success'
    },
    
    // 获取成果标题
    getResultTitle(index) {
      const titles = [
        this.$t('specialContainer.case.safeEfficientDelivery'),
        this.$t('specialContainer.case.customerSatisfaction'),
        this.$t('specialContainer.case.professionalRecognition'),
        this.$t('specialContainer.case.successCase'),
        this.$t('specialContainer.case.excellentResults')
      ]
      return titles[index % titles.length] || this.$t('specialContainer.case.successfulDelivery')
    },
    
    // 获取技术参数标签
    getSpecificationLabel(key) {
      const labels = {
        'container_type': this.$t('specialContainer.case.containerType'),
        'equipment_weight': this.$t('specialContainer.case.equipmentWeight'),
        'equipment_height': this.$t('specialContainer.case.equipmentHeight'),
        'transportation_time': this.$t('specialContainer.case.transportationTime'),
        'route': this.$t('specialContainer.case.route')
      }
      return labels[key] || key
    },
    
    async fetchCaseData() {
      try {
        this.loading = true
        // 获取当前语言
        const currentLanguage = localStorage.getItem('preferred-language') || 'zh-CN'
        
        // 获取案例列表，根据当前语言自动筛选
        const response = await getCaseList(currentLanguage)
        if (response.data.code === 200 && response.data.data && response.data.data.length > 0) {
          const rawData = response.data.data[0] // 获取列表中的第一个案例
          
          // 解析JSON字符串字段
          const parseJsonField = (field, defaultValue) => {
            if (!field) return defaultValue
            try {
              return JSON.parse(field)
            } catch (e) {
              console.warn('JSON解析失败:', e)
              return defaultValue
            }
          }
          
          // 处理数据，解析JSON字符串
          this.caseData = {
            ...rawData,
            challenge: parseJsonField(rawData.challenge, { description: '' }),
            solutions: this.parseSolutionsField(rawData.solutions),
            results: this.parseResultsField(rawData.results),
            specifications: this.parseSpecificationsField(rawData.specifications),
            images: parseJsonField(rawData.images, []),
            client: parseJsonField(rawData.client, { name: '', industry: '', requirement: '' })
          }
          
          console.log('获取案例数据成功:', this.caseData)
          console.log('解决方案数据:', this.caseData.solutions)
          console.log('解决方案原始数据:', rawData.solutions)
        } else {
          this.error = '获取案例数据失败或暂无案例数据'
        }
      } catch (err) {
        this.error = '网络错误，请稍后重试'
        console.error('获取案例数据失败:', err)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.fetchCaseData()
  }
}
</script>

<style lang="scss" scoped>
.special-case-container {
  position: relative;
  overflow-x: hidden;
}

// 横幅区域
.banner-section {
  width: 100%;
  height: 500px;
  background: linear-gradient(135deg, rgba(26, 115, 232, 0.9), rgba(52, 211, 153, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    height: 350px;
    padding: 0 20px;
  }

  .banner-content {
    max-width: 800px;
    padding: 0 20px;

    .banner-title {
      font-size: 3rem;
      font-weight: 700;
      margin-bottom: 20px;
      text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.5);

      @media (max-width: 768px) {
        font-size: 2rem;
      }
    }

    .banner-subtitle {
      font-size: 1.3rem;
      margin-bottom: 25px;
      opacity: 0.9;

      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }

    .banner-desc {
      font-size: 1rem;
      line-height: 1.6;
      opacity: 0.9;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
  }
}

// 通用容器
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// 案例详情
.case-detail {
  padding: 80px 0;

  .case-header {
    text-align: center;
    margin-bottom: 60px;

    .case-title {
      font-size: 2.2rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 20px;
      line-height: 1.3;

      @media (max-width: 768px) {
        font-size: 1.8rem;
      }
    }

    .case-meta {
      display: flex;
      justify-content: center;
      gap: 15px;

      .case-category,
      .case-date {
        background: linear-gradient(135deg, #1a73e8, #34d399);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 500;
      }
    }
  }
}

// 通用标题样式
.section-header {
  margin-bottom: 40px;

  .section-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 15px;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .title-decoration {
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #1a73e8, #34d399);
    border-radius: 2px;
  }
}

// 案例内容区域
.case-section {
  margin-bottom: 80px;

  .section-content {
    p {
      font-size: 1.1rem;
      line-height: 1.8;
      color: #4a5568;
      margin-bottom: 20px;

      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }
  }
}

// 解决方案网格
.solution-grid {
  display: grid;
  gap: 40px;

  .solution-item {
    display: flex;
    align-items: flex-start;
    padding: 30px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
    }

    .solution-icon {
      width: 70px;
      height: 70px;
      background: linear-gradient(135deg, #1a73e8, #34d399);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.8rem;
      margin-right: 25px;
      flex-shrink: 0;

      @media (max-width: 768px) {
        margin-right: 0;
        margin-bottom: 20px;
      }
    }

    .solution-content {
      flex: 1;

      h4 {
        font-size: 1.3rem;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 15px;
      }

      p {
        font-size: 1rem;
        line-height: 1.7;
        color: #64748b;
      }
    }
  }
}

// 图片画廊
.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;

  .gallery-item {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    img {
      width: 100%;
      height: 200px;
      object-fit: cover;
    }

    .image-caption {
      padding: 15px;
      text-align: center;
      font-size: 0.9rem;
      color: #64748b;
      background: #f8fafc;
    }
  }
}

// 成果展示
.result-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;

  .result-card {
    display: flex;
    align-items: center;
    padding: 25px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
    }

    .result-icon {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #1a73e8, #34d399);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      margin-right: 20px;
      flex-shrink: 0;

      @media (max-width: 768px) {
        margin-right: 0;
        margin-bottom: 15px;
      }
    }

    .result-text {
      h4 {
        font-size: 1.2rem;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 10px;
      }

      p {
        font-size: 0.95rem;
        color: #64748b;
        line-height: 1.6;
      }
    }
  }
}

// 技术参数
.tech-specs {
  .specs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;

    .spec-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
      border-left: 4px solid #1a73e8;

      .spec-label {
        font-weight: 600;
        color: #4a5568;
      }

      .spec-value {
        font-weight: 700;
        color: #1a73e8;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .case-detail {
    padding: 50px 0;
  }

  .case-section {
    margin-bottom: 50px;
  }

  .solution-grid,
  .result-content {
    grid-template-columns: 1fr;
  }

  .image-gallery {
    grid-template-columns: 1fr;
  }

  .specs-grid {
    grid-template-columns: 1fr !important;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1a73e8;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.1rem;
    color: #64748b;
  }
}

// 暂无数据样式
.no-data {
  text-align: center;
  padding: 40px 0;
  color: #64748b;
  font-size: 1.1rem;
}

// 错误状态
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;

  .error-icon {
    font-size: 3rem;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.5rem;
    color: #dc2626;
    margin-bottom: 15px;
  }

  p {
    font-size: 1rem;
    color: #64748b;
    margin-bottom: 25px;
    max-width: 400px;
  }

  .retry-btn {
    background: linear-gradient(135deg, #1a73e8, #34d399);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}

// 动画效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.special-case-container > div {
  animation: fadeInUp 0.6s ease forwards;
}
</style>
